/**
 * Studio DocScope / numbered keys → Word [[if SECTION]] 반복 행 배열.
 */

function filled(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Studio DocScope 키 (indem_1/d_name …) → Word [[if directors]] 반복 행.
 * @param ensureKeys 행에 항상 포함할 Word 키 (없으면 ''). 누락 시 [[d_witness_name]] 등이
 *   남아 전역 치환으로 다른 행 값이 새는 것을 막는다.
 */
export function buildScopedRepeatingRows(
  values: Record<string, any>,
  scopePrefix: string,
  ensureKeys: string[] = [],
): Record<string, string>[] {
  const idSet = new Set<number>();
  const re = new RegExp(`^${escapeRe(scopePrefix)}(\\d+)/`);
  for (const k of Object.keys(values || {})) {
    const m = k.match(re);
    if (m) idSet.add(Number(m[1]));
  }
  const ids = [...idSet].sort((a, b) => a - b);
  const rows: Record<string, string>[] = [];
  for (const id of ids) {
    const scope = `${scopePrefix}${id}/`;
    const row: Record<string, string> = {};
    for (const key of ensureKeys) row[key] = '';
    let any = false;
    for (const [k, v] of Object.entries(values || {})) {
      if (!k.startsWith(scope)) continue;
      const bare = k.slice(scope.length);
      if (!bare) continue;
      const s = filled(v);
      row[bare] = s;
      if (s) any = true;
    }
    if (any) rows.push(row);
  }
  return rows;
}

/** Letter of Indemnity — Word [[if directors]] 행 키 */
export const INDEMNITY_DIRECTOR_KEYS = [
  'd_name',
  'd_identification_no',
  'd_signature_date',
  'd_witness_name',
  'd_witness_identification_no',
  'd_witness_date',
] as const;

export function buildIndemnityDirectorRows(values: Record<string, any>): Record<string, string>[] {
  return buildScopedRepeatingRows(values, 'indem_', [...INDEMNITY_DIRECTOR_KEYS]).filter(
    (r) => filled(r.d_name),
  );
}

/** First Directors Resolution — directors / allottees */
export const FIRST_RES_DIRECTOR_KEYS = ['d_name'] as const;
export const FIRST_RES_ALLOTTEE_KEYS = [
  'a_name',
  'a_number_of_shares',
  'a_share_certificate_number',
] as const;

/**
 * Studio numbered suffix keys (entity_name_1) → Word rows with remapped field names.
 * fieldMap: studioStem → wordKey
 */
export function buildNumberedSuffixRows(
  values: Record<string, any>,
  fieldMap: Record<string, string>,
): Record<string, string>[] {
  const stems = Object.keys(fieldMap);
  const idSet = new Set<number>();
  for (const k of Object.keys(values || {})) {
    for (const stem of stems) {
      const m = k.match(new RegExp(`^${escapeRe(stem)}_(\\d+)$`));
      if (m) idSet.add(Number(m[1]));
    }
  }
  const ids = [...idSet].sort((a, b) => a - b);
  const rows: Record<string, string>[] = [];
  for (const id of ids) {
    const row: Record<string, string> = {};
    let any = false;
    for (const stem of stems) {
      const s = filled(values[`${stem}_${id}`]);
      row[fieldMap[stem]] = s;
      if (s) any = true;
    }
    if (any) rows.push(row);
  }
  return rows;
}

/**
 * Studio infix id keys (c_1_name) → Word rows.
 * fieldMap: studioTail → wordKey  (name → c_name)
 */
export function buildInfixIdRows(
  values: Record<string, any>,
  prefix: string,
  fieldMap: Record<string, string>,
): Record<string, string>[] {
  const tails = Object.keys(fieldMap);
  const idSet = new Set<number>();
  for (const k of Object.keys(values || {})) {
    for (const tail of tails) {
      const m = k.match(new RegExp(`^${escapeRe(prefix)}_(\\d+)_${escapeRe(tail)}$`));
      if (m) idSet.add(Number(m[1]));
    }
  }
  const ids = [...idSet].sort((a, b) => a - b);
  const rows: Record<string, string>[] = [];
  for (const id of ids) {
    const row: Record<string, string> = {};
    let any = false;
    for (const tail of tails) {
      const s = filled(values[`${prefix}_${id}_${tail}`]);
      row[fieldMap[tail]] = s;
      if (s) any = true;
    }
    if (any) rows.push(row);
  }
  return rows;
}

const DDF_S165_MAP = {
  holder: 's_registered_holder',
  desc: 's_description',
  amount: 's_amount_at_appointment',
  balance: 's_balance_at_end',
  acquired: 's_date_acquired_disposed',
} as const;

/** DDF 한 부(formDataForScope 결과) → Word 반복 섹션 배열 */
export function attachDdfRepeatingSections(fd: Record<string, any>): Record<string, any> {
  const out = { ...fd };
  const s156 = buildNumberedSuffixRows(fd, {
    entity_name: 'entity_name',
    entity_nature: 'entity_nature_of_interest',
  });
  if (s156.length) out.section156_entities = s156;

  const own = buildNumberedSuffixRows(fd, {
    own_holder: DDF_S165_MAP.holder,
    own_desc: DDF_S165_MAP.desc,
    own_amount: DDF_S165_MAP.amount,
    own_balance: DDF_S165_MAP.balance,
    own_acquired: DDF_S165_MAP.acquired,
  });
  if (own.length) out.section165_own_name = own;

  const fam = buildNumberedSuffixRows(fd, {
    fam_holder: DDF_S165_MAP.holder,
    fam_desc: DDF_S165_MAP.desc,
    fam_amount: DDF_S165_MAP.amount,
    fam_balance: DDF_S165_MAP.balance,
    fam_acquired: DDF_S165_MAP.acquired,
  });
  if (fam.length) out.section165_family = fam;

  const od = buildNumberedSuffixRows(fd, {
    od_holder: DDF_S165_MAP.holder,
    od_desc: DDF_S165_MAP.desc,
    od_amount: DDF_S165_MAP.amount,
    od_balance: DDF_S165_MAP.balance,
    od_acquired: DDF_S165_MAP.acquired,
  });
  if (od.length) out.section165_other_deemed = od;

  const bc = buildNumberedSuffixRows(fd, {
    bc_holder: DDF_S165_MAP.holder,
    bc_desc: DDF_S165_MAP.desc,
    bc_amount: DDF_S165_MAP.amount,
    bc_balance: DDF_S165_MAP.balance,
    bc_acquired: DDF_S165_MAP.acquired,
  });
  if (bc.length) out.section165_bodies_corporate = bc;

  return out;
}

const NOMINEE_X_MAP = {
  name: 'x_name',
  id: 'x_identification_no',
  address: 'x_address',
  nominator: 'x_is_nominator',
} as const;

/** Nominee Director Agreement → Word 반복 섹션 배열 */
export function attachNomineeRepeatingSections(fd: Record<string, any>): Record<string, any> {
  const out = { ...fd };

  const ap = buildNumberedSuffixRows(fd, {
    ap_name: 'ap_name',
    ap_witness_name: 'ap_witness_name',
    ap_witness_id: 'ap_witness_identification_no',
  });
  if (ap.length) out.ap = ap;

  const principals = buildNumberedSuffixRows(fd, {
    prin_name: NOMINEE_X_MAP.name,
    prin_id: NOMINEE_X_MAP.id,
    prin_address: NOMINEE_X_MAP.address,
    prin_nominator: NOMINEE_X_MAP.nominator,
  });
  if (principals.length) out.principals = principals;

  const directorsList = buildNumberedSuffixRows(fd, {
    ndir_name: NOMINEE_X_MAP.name,
    ndir_id: NOMINEE_X_MAP.id,
    ndir_address: NOMINEE_X_MAP.address,
    ndir_nominator: NOMINEE_X_MAP.nominator,
  });
  if (directorsList.length) out.directors_list = directorsList;

  const shareholdersList = buildNumberedSuffixRows(fd, {
    nsh_name: NOMINEE_X_MAP.name,
    nsh_id: NOMINEE_X_MAP.id,
    nsh_address: NOMINEE_X_MAP.address,
    nsh_nominator: NOMINEE_X_MAP.nominator,
  });
  if (shareholdersList.length) out.shareholders_list = shareholdersList;

  const auth = buildNumberedSuffixRows(fd, {
    auth_name: NOMINEE_X_MAP.name,
    auth_id: NOMINEE_X_MAP.id,
    auth_address: NOMINEE_X_MAP.address,
    auth_nominator: NOMINEE_X_MAP.nominator,
  });
  if (auth.length) out.authorised_persons_table = auth;

  return out;
}

/** Welcome Letter → Word [[if client_contacts]] */
export function attachWelcomeRepeatingSections(fd: Record<string, any>): Record<string, any> {
  const out = { ...fd };
  const contacts = buildInfixIdRows(fd, 'c', {
    name: 'c_name',
    contact_no: 'c_contact_no',
    email: 'c_email',
  });
  if (contacts.length) out.client_contacts = contacts;
  return out;
}
